import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import lib from '../assets/utils/lib';

const DEFAULT_POLL_INTERVAL_MS = 15000;

function unwrapPaAgentDecisionsResponse(resp) {
  if (!resp || resp.status === 'error' || Number(resp.error) === 1) {
    return { ok: false, rows: [], pending: undefined, httpStatus: resp?.httpStatus };
  }
  const envelope = (resp.data && !Array.isArray(resp.data)) ? resp.data : resp;
  if (Number(envelope?.error) === 1) {
    return { ok: false, rows: [], pending: undefined, httpStatus: resp?.httpStatus };
  }
  const inner = (envelope?.data && !Array.isArray(envelope.data)) ? envelope.data : envelope;
  const rows = Array.isArray(inner?.decisions)
    ? inner.decisions
    : Array.isArray(envelope?.decisions)
      ? envelope.decisions
      : [];
  return {
    ok: true,
    rows,
    pending: inner?.pending ?? envelope?.pending,
    httpStatus: resp?.httpStatus ?? (resp?.config ? resp.status : undefined),
  };
}

/**
 * Encounter-level SaaSPro advisory decisions hook.
 *
 * Behaviour (per the SaaSPro integration guide):
 *  - Seeds from `paList.agent_decisions` when the encounter payload already
 *    includes advisories (click-through from PA Agent audit).
 *  - Fetches `GET /v2/pa/agent/decisions?checkin_id=…&current_only=false` so
 *    advisories remain visible after human approve/reject (status applied/superseded).
 *  - Polls every `pollIntervalMs` ms while the tab is visible and at least one
 *    claim item is still waiting for a decision (i.e. `hasPending === true`).
 *  - Treats any non-200 / error response as `silently absent` — callers receive
 *    `decisions: []` and no error toasts are fired here. The PA detail page
 *    must never break because of this integration.
 *
 * @param {string|null|undefined} checkinId
 * @param {{
 *   claimItemIds?: Array<string|number>,
 *   pollIntervalMs?: number,
 *   enabled?: boolean,
 * }} [opts]
 */
export default function useSaaSProAgentDecisions(checkinId, opts = {}) {
  const {
    claimItemIds = [],
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    enabled = true,
    seedDecisions,
    seedPending,
  } = opts;

  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingState, setPendingState] = useState({ pending: true });
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const itemIdsKey = useMemo(() => {
    return (claimItemIds || [])
      .map((v) => String(v))
      .sort()
      .join('|');
  }, [claimItemIds]);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchDecisions = useCallback(async ({ silent = false } = {}) => {
    if (!checkinId || !enabled) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (!silent) setLoading(true);
    try {
      const resp = await lib.getEncounterPaAgentDecisions(checkinId, { current_only: 'false' });
      const parsed = unwrapPaAgentDecisionsResponse(resp);
      if (mountedRef.current) {
        setLoaded(true);
        if (!parsed.ok) {
          // Keep polling; do not treat a failed overlay GET as "no AI on this encounter".
          setPendingState((prev) => ({ pending: prev?.pending ?? true }));
          return;
        }
        // Never clobber a non-empty seed/previous result with an empty GET.
        if (parsed.rows.length) {
          setDecisions(parsed.rows);
          if (parsed.pending != null) {
            setPendingState({ pending: Boolean(parsed.pending) });
          } else {
            setPendingState({});
          }
        }
      }
    } catch (e) {
      if (mountedRef.current) {
        setLoaded(true);
        setPendingState({ pending: true });
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [checkinId, enabled]);

  // Group decisions by claim_item_id (latest wins via received_at desc).
  const decisionsByItem = useMemo(() => {
    const map = {};
    const sorted = [...(decisions || [])].sort((a, b) => {
      const aTs = new Date(a?.received_at || a?.decided_at || 0).getTime();
      const bTs = new Date(b?.received_at || b?.decided_at || 0).getTime();
      return bTs - aTs;
    });
    for (const d of sorted) {
      const key = d?.claim_item_id == null ? null : String(d.claim_item_id);
      if (key == null) continue;
      if (!map[key]) map[key] = d;
    }
    return map;
  }, [decisions]);

  const overall = useMemo(() => {
    const withOverall = (decisions || []).find((d) => d?.overall_recommendation);
    if (!withOverall) return null;
    return {
      recommendation: withOverall.overall_recommendation,
      rationale: withOverall.overall_rationale,
      received_at: withOverall.received_at,
    };
  }, [decisions]);

  const ids = useMemo(() => itemIdsKey.split('|').filter(Boolean), [itemIdsKey]);
  const allMissing = useMemo(() => {
    if (!loaded) return false;
    // No advisories and API did not report an in-flight outbox → treat as "no AI".
    return (decisions || []).length === 0 && !pendingState?.pending;
  }, [decisions, loaded, pendingState]);

  const hasPending = useMemo(() => {
    if (!loaded) return true;
    // Prefer explicit API pending (outbox sent, waiting on first advisory).
    if (pendingState?.pending) return true;
    if (allMissing) return false;
    if (!ids.length) return false;
    // Partial advisories: still waiting on lines that have no decision yet.
    return ids.some((id) => !decisionsByItem[String(id)]);
  }, [ids, decisionsByItem, loaded, pendingState, allMissing]);

  // Initial fetch + reset when encounter changes.
  useEffect(() => {
    setDecisions([]);
    setLoaded(false);
    setPendingState({ pending: true });
    if (!checkinId || !enabled) return;
    fetchDecisions({ silent: false });
  }, [checkinId, enabled, fetchDecisions]);

  // Apply advisories already returned on paList / GET /v2/pa/{id}.
  useEffect(() => {
    if (!Array.isArray(seedDecisions)) return;
    setDecisions((prev) => (seedDecisions.length || !prev.length) ? seedDecisions : prev);
    setLoaded(true);
    if (seedPending != null) {
      setPendingState({ pending: Boolean(seedPending) });
    } else if (seedDecisions.length) {
      setPendingState({});
    }
  }, [seedDecisions, seedPending]);

  // Polling: only while page is visible AND there's still work pending.
  useEffect(() => {
    clearTimer();
    if (!checkinId || !enabled) return undefined;
    if (!hasPending) return undefined;
    if (typeof document !== 'undefined' && document.hidden) return undefined;

    timerRef.current = setTimeout(() => {
      fetchDecisions({ silent: true });
    }, pollIntervalMs);

    return clearTimer;
  }, [checkinId, enabled, hasPending, pollIntervalMs, decisions, fetchDecisions, clearTimer]);

  // React to tab visibility changes — refresh on focus, stop polling when hidden.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibility = () => {
      if (document.hidden) {
        clearTimer();
      } else if (checkinId && enabled && hasPending) {
        fetchDecisions({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [checkinId, enabled, hasPending, fetchDecisions, clearTimer]);

  const refresh = useCallback(() => fetchDecisions({ silent: false }), [fetchDecisions]);

  return {
    decisions,
    decisionsByItem,
    overall,
    loading,
    loaded,
    hasPending,
    allMissing,
    refresh,
  };
}
